import { useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Flex, List, Modal, Progress, Radio, Space, Typography } from "antd";
import { useState, type ReactNode } from "react";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";

/** The order fields the shipping actions need. */
export type ShipTarget = { id: string; externalId: string; platform: ChannelPlatformName };
type Method = "pickup" | "dropoff";
type BulkResult = { id: string; ok: boolean; error?: string; message?: string };

// The server takes these many per request (openapi: maxItems); larger
// selections go in chunks, which also drives the progress shown.
const ARRANGE_CHUNK = 50;
const PREPARE_CHUNK = 20;

function chunks<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

function useRefreshOrders() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };
}

/** "Atur pengiriman" for one or several orders. */
export function ArrangeModal({ targets, onClose }: { targets: ShipTarget[] | null; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshOrders();
  const [method, setMethod] = useState<Method>("pickup");
  const [busy, setBusy] = useState(false);
  const open = targets !== null && targets.length > 0;
  const hasLazada = targets?.some((o) => o.platform === "lazada") ?? false;
  const hasBlibli = targets?.some((o) => o.platform === "blibli") ?? false;

  const submit = async () => {
    if (!targets) return;
    setBusy(true);
    try {
      const [only] = targets;
      if (targets.length === 1 && only) {
        await unwrap(api.POST("/v1/orders/{orderID}/arrange-shipment", { params: { path: { orderID: only.id } }, body: { method } }));
        void message.success(t("orders.arranged"));
      } else {
        const results: BulkResult[] = [];
        for (const part of chunks(targets.map((o) => o.id), ARRANGE_CHUNK)) {
          results.push(...(await unwrap(api.POST("/v1/orders/arrange-shipment", { body: { ids: part, method } }))).results);
        }
        const ok = results.filter((r) => r.ok).length;
        if (ok === results.length) void message.success(t("orders.arrangedBulk", { ok }));
        else void message.warning(t("orders.arrangedPartial", { ok, failed: results.length - ok }));
      }
      onClose();
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <Modal
      open={open}
      title={targets?.length === 1 ? t("orders.arrangeTitle", { number: targets[0]?.externalId ?? "" }) : t("orders.arrangeTitleBulk", { n: targets?.length ?? 0 })}
      okText={t("orders.arrange")}
      cancelText={t("common.cancel")}
      confirmLoading={busy}
      onOk={() => void submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("orders.arrangeBody")}</Typography.Paragraph>
      <Typography.Text strong>{t("orders.method")}</Typography.Text>
      <Radio.Group
        value={method}
        onChange={(e) => setMethod(e.target.value as Method)}
        style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}
        options={[
          { value: "pickup", label: t("orders.method.pickup") },
          { value: "dropoff", label: t("orders.method.dropoff") },
        ]}
      />
      {hasLazada && <Alert type="info" showIcon style={{ marginTop: 16 }} title={t("orders.methodLazada")} />}
      {hasBlibli && <Alert type="info" showIcon style={{ marginTop: 16 }} title={t("orders.methodBlibli")} />}
    </Modal>
  );
}

type Failure = { target: ShipTarget; text: string };

/**
 * Printing labels: prepare in chunks (marketplaces take a moment per label),
 * then one PDF opened in a new tab for the browser's print dialog. The tab is
 * opened at the click, before any waiting, so pop-up blockers let it through.
 */
export function useLabelPrinter(): { print: (targets: ShipTarget[], opts?: { original?: boolean }) => void; ui: ReactNode } {
  const { t, tDynamic } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshOrders();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<{ failed: Failure[]; ready: ShipTarget[] } | null>(null);

  const reasonOf = (r: BulkResult) => (r.message ? `${tDynamic(`error.${r.error}`, "error.generic")} (${r.message})` : tDynamic(`error.${r.error}`, "error.generic"));

  const openPdf = async (ids: string[], original: boolean, win: Window | null) => {
    try {
      const blob = await unwrap(api.POST("/v1/orders/labels", { body: { ids, ...(original ? { original } : {}) }, parseAs: "blob" }));
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) win.location.href = url;
      else if (!window.open(url, "_blank")) void message.warning(t("orders.popupBlocked"));
      // The tab keeps its own copy once loaded.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      win?.close();
      void message.error(errorText(err));
    } finally {
      refresh();
    }
  };

  const print = (targets: ShipTarget[], opts: { original?: boolean } = {}) => {
    const win = window.open("", "_blank");
    if (win) win.document.title = t("orders.printing");
    void (async () => {
      setProgress({ done: 0, total: targets.length });
      const failed: Failure[] = [];
      const ready: ShipTarget[] = [];
      try {
        for (const part of chunks(targets, PREPARE_CHUNK)) {
          const { results } = await unwrap(api.POST("/v1/orders/labels/prepare", { body: { ids: part.map((o) => o.id) } }));
          for (const r of results) {
            const target = part.find((o) => o.id === r.id)!;
            if (r.ok) ready.push(target);
            else failed.push({ target, text: reasonOf(r) });
          }
          setProgress((p) => p && { ...p, done: p.done + part.length });
        }
      } catch (err) {
        win?.close();
        setProgress(null);
        void message.error(errorText(err));
        return;
      }
      setProgress(null);
      if (failed.length === 0) {
        await openPdf(ready.map((o) => o.id), opts.original ?? false, win);
        return;
      }
      // Some cannot print: say which and why, and let the user print the rest
      // with a fresh click (the tab opened earlier is not needed then).
      win?.close();
      setFailures({ failed, ready });
    })();
  };

  const ui = (
    <>
      <Modal open={progress !== null} title={t("orders.preparingTitle")} footer={null} closable={false} mask={{ closable: false }}>
        {progress && (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Progress percent={Math.round((progress.done / Math.max(progress.total, 1)) * 100)} />
            <Typography.Text type="secondary">{t("orders.preparing", { done: progress.done, total: progress.total })}</Typography.Text>
          </Space>
        )}
      </Modal>
      <Modal
        open={failures !== null}
        title={t("orders.labelFailedTitle", { n: failures?.failed.length ?? 0 })}
        onCancel={() => setFailures(null)}
        footer={
          <Flex justify="end" gap={8}>
            <Button onClick={() => setFailures(null)}>{t("common.close")}</Button>
            {(failures?.ready.length ?? 0) > 0 && (
              <Button
                type="primary"
                onClick={() => {
                  const ids = failures!.ready.map((o) => o.id);
                  setFailures(null);
                  void openPdf(ids, false, window.open("", "_blank"));
                }}
              >
                {t("orders.printReady", { n: failures?.ready.length ?? 0 })}
              </Button>
            )}
          </Flex>
        }
      >
        {(failures?.ready.length ?? 0) > 0 && <Typography.Paragraph type="secondary">{t("orders.labelFailedBody")}</Typography.Paragraph>}
        <List
          size="small"
          dataSource={failures?.failed ?? []}
          renderItem={(f) => (
            <List.Item>
              <Space orientation="vertical" size={0}>
                <Typography.Text strong className="z-num">
                  {f.target.externalId}
                </Typography.Text>
                <Typography.Text type="secondary">{f.text}</Typography.Text>
              </Space>
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
  return { print, ui };
}
